import { get } from 'lodash';

import { type VendorDeviceMeta, normalizeVendorDeviceMeta } from '@/delivery-type-plugin/components/vendorDevice';
import i18n from '@/i18n';

import { getDataVolumeStorageGi, getVolumePvcTemplate } from '../../../../capabilities/maas-model-storage/storageVolume';
import { getDataDiskMode, parsePrimaryDataVolumeField } from '../../../../capabilities/maas-node-groups/masterGroupForm';
import { DEFAULT_QUANTITY_UNITS, normalizeQuantityUnit } from '../../../../components/quantityUnit';
import type { ResourceType } from '../../../../platform/pipeline-kernel/types';
import type { DisplayRow, StorageFormValues, StorageVolumeResourceOptions, ToResourceConfigFn } from './types';

/** 轻量解析应用存储组（避免 _shared → AppInstance 依赖） */
function parseAppStorageGroupVolumes(raw: unknown): Record<string, unknown>[] {
  if (raw == null || raw === '') return [];
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const volume = (item as Record<string, unknown>).volume;
    if (!volume || typeof volume !== 'object' || Array.isArray(volume)) return [];
    const volumeObj = volume as Record<string, unknown>;
    if (!getDataDiskMode(volumeObj)) return [];
    return [volumeObj];
  });
}

function getConfiguredStorageVolumes(values: StorageFormValues) {
  const fromGroups = parseAppStorageGroupVolumes(values._appStorageGroups);
  if (fromGroups.length > 0) return fromGroups;

  const primaryVolume = parsePrimaryDataVolumeField(values._primaryDataVolume);
  if (primaryVolume && getDataDiskMode(primaryVolume)) {
    return [primaryVolume];
  }
  return [];
}

function forEachConfiguredStorageVolume(
  values: StorageFormValues,
  callback: (volume: Record<string, unknown>, displayIndex: number) => void,
) {
  getConfiguredStorageVolumes(values).forEach((volume, displayIndex) => {
    callback(volume, displayIndex);
  });
}

export function appendAggregatedStorageVolumeDisplayRow(
  values: StorageFormValues,
  pushRow: (item: DisplayRow) => void,
  unit: string,
  options?: StorageVolumeResourceOptions,
) {
  let totalGi = 0;
  let hasValue = false;

  forEachConfiguredStorageVolume(values, (volume, displayIndex) => {
    const storageGi = options?.resolveStorageGi?.(volume, displayIndex) ?? getDataVolumeStorageGi(volume);
    if (storageGi == null || storageGi <= 0) return;
    totalGi += storageGi;
    hasValue = true;
  });

  if (!hasValue) return;

  // 存储容量展示固定 Gi（忽略计费项 quantityUnit）
  const displayUnit = unit || DEFAULT_QUANTITY_UNITS.disk;
  pushRow({
    id: 'shared-storage-total',
    label: `${i18n.t('data-center:shared.volume')}大小 (总)`,
    value: displayUnit ? `${totalGi} ${displayUnit}` : String(totalGi),
  });
}

export function appendStorageVolumeResourceConfigs(
  result: ResourceType[],
  template: ResourceType,
  values: StorageFormValues,
  toResourceConfig: ToResourceConfigFn,
  options?: StorageVolumeResourceOptions,
) {
  forEachConfiguredStorageVolume(values, (volume, displayIndex) => {
    const storageGi = options?.resolveStorageGi?.(volume, displayIndex) ?? getDataVolumeStorageGi(volume);
    const storageClass = get(getVolumePvcTemplate(volume), 'storageClassName') as string | undefined;
    const pricing = normalizeVendorDeviceMeta(volume.pricing as VendorDeviceMeta | undefined);
    const storageConfig = toResourceConfig(template, {
      vendor: pricing?.vendor ?? storageClass,
      device: pricing?.device,
      deviceType: pricing?.deviceType,
      vendorID: pricing?.vendorID,
      deviceID: pricing?.deviceID,
      requirement: storageGi,
      quantityUnit: normalizeQuantityUnit(pricing?.quantityUnit),
    });
    if (storageConfig) result.push(storageConfig);
  });
}
